import React, { useEffect, useState } from 'react';
import useQualityStore from '../store/qualityStore';
import useProjectStore from '../store/projectStore';
import useAuthStore from '../store/authStore';
import useUIStore from '../store/uiStore';
import { checkChecklistItem, getPendingVerification } from '../services/qualityService';
import { formatDateDDMMYY } from '../utils/formatDate';

const initialForm = { name: '', checklist_type: 'intermediate', is_template: true };
const initialItem = { description: '', spec: '', is_critical: false };

const QualityChecklistsPage = () => {
  const { checklists, isLoading, error, fetchChecklists, createChecklist, cloneChecklistToProject } = useQualityStore();
  const { projects, fetchProjects } = useProjectStore();
  const { user } = useAuthStore();
  const showToast = useUIStore((s) => s.showToast);

  const [form, setForm] = useState(initialForm);
  const [items, setItems] = useState([{ ...initialItem }]);
  const [cloneTargets, setCloneTargets] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [pending, setPending] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);

  const loadPending = () => {
    setPendingLoading(true);
    getPendingVerification()
      .then((res) => setPending(res.data.pending || []))
      .catch(() => {})
      .finally(() => setPendingLoading(false));
  };

  useEffect(() => {
    fetchChecklists();
    fetchProjects();
    loadPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) showToast(error, 'error');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const updateItem = (idx, patch) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () => setItems((prev) => [...prev, { ...initialItem }]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      showToast('Checklist name is required', 'error');
      return;
    }
    const payload = {
      ...form,
      items: items
        .filter((it) => it.description.trim())
        .map((it, idx) => ({ id: `item_${idx + 1}`, ...it })),
      created_by_id: user?.id,
    };
    const result = await createChecklist(payload);
    if (result.success) {
      showToast('Checklist created', 'success');
      setForm(initialForm);
      setItems([{ ...initialItem }]);
    } else {
      showToast(result.error, 'error');
    }
  };

  const handleClone = async (checklistId) => {
    const projectId = cloneTargets[checklistId];
    if (!projectId) {
      showToast('Select a project to clone into', 'error');
      return;
    }
    const result = await cloneChecklistToProject(checklistId, { project_id: Number(projectId) });
    if (result.success) {
      showToast('Cloned to project', 'success');
    } else {
      showToast(result.error, 'error');
    }
  };

  const handleToggleItem = async (checklistId, item, stage) => {
    const currentlyChecked = stage === 'quality' ? item.quality_checked : item.production_checked;
    try {
      await checkChecklistItem(checklistId, item.id, !currentlyChecked, user?.id, stage);
      fetchChecklists();
      loadPending();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update item', 'error');
    }
  };

  const handleVerifyPending = async (row) => {
    try {
      await checkChecklistItem(row.checklist_id, row.item_id, true, user?.id, 'quality');
      showToast('Item verified', 'success');
      fetchChecklists();
      loadPending();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to verify item', 'error');
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-xl font-bold text-white mb-6">Quality Checklists</h1>

        <div className="glass-card mb-6">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Pending Verification</h2>
            <p className="text-xs text-gray-500 mt-0.5">Items production has checked that still need your sign-off.</p>
          </div>
          {pendingLoading ? (
            <p className="text-gray-500 text-sm p-5">Loading...</p>
          ) : pending.length === 0 ? (
            <p className="text-gray-400 text-sm p-5">Nothing waiting on verification.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {pending.map((row) => (
                <li key={`${row.checklist_id}-${row.item_id}`} className="px-5 py-3 text-sm flex items-center justify-between gap-3">
                  <div>
                    <span>{row.item_description}</span>
                    {row.is_critical && <span className="ml-1 text-red-600 font-semibold text-xs">(critical)</span>}
                    <div className="text-xs text-gray-400">
                      {row.project_name} · {row.checklist_name} · checked by {row.production_checked_by}, {formatDateDDMMYY(row.production_checked_date)}
                    </div>
                  </div>
                  <button
                    onClick={() => handleVerifyPending(row)}
                    className="text-xs px-3 py-1.5 rounded bg-komal-blue text-white font-semibold whitespace-nowrap"
                  >
                    Verify
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <form onSubmit={handleSubmit} className="glass-card p-6 mb-6 space-y-4">
          <h2 className="font-semibold text-gray-900">New Checklist</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={form.checklist_type}
                onChange={(e) => setForm({ ...form, checklist_type: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="intermediate">Intermediate</option>
                <option value="final_inspection">Final Inspection (FAT/SAT)</option>
              </select>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_template}
              onChange={(e) => setForm({ ...form, is_template: e.target.checked })}
            />
            Save as reusable template
          </label>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Checklist Items</label>
            {items.map((it, idx) => (
              <div key={idx} className="flex gap-2 items-center mb-2">
                <input
                  placeholder="Description"
                  value={it.description}
                  onChange={(e) => updateItem(idx, { description: e.target.value })}
                  className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
                />
                <input
                  placeholder="Spec"
                  value={it.spec}
                  onChange={(e) => updateItem(idx, { spec: e.target.value })}
                  className="w-40 border border-gray-300 rounded px-3 py-2 text-sm"
                />
                <label className="flex items-center gap-1 text-xs text-gray-500">
                  <input
                    type="checkbox"
                    checked={it.is_critical}
                    onChange={(e) => updateItem(idx, { is_critical: e.target.checked })}
                  />
                  Critical
                </label>
                <button type="button" onClick={() => removeItem(idx)} className="text-red-500 hover:text-red-700 text-xs">
                  Remove
                </button>
              </div>
            ))}
            <button type="button" onClick={addItem} className="text-sm text-komal-blue hover:underline">
              + Add item
            </button>
          </div>

          <button
            type="submit"
            className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold"
          >
            Create Checklist
          </button>
        </form>

        <div className="glass-card">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">All Checklists</h2>
          </div>
          {isLoading ? (
            <p className="text-gray-500 text-sm p-5">Loading...</p>
          ) : checklists.length === 0 ? (
            <p className="text-gray-500 text-sm p-5">No checklists yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {checklists.map((c) => (
                <li key={c.id} className="px-5 py-3 text-sm">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <button
                      onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
                      className="text-left flex-1 flex items-center gap-1"
                    >
                      <span className={`text-gray-400 text-xs transition-transform ${expandedId === c.id ? 'rotate-90' : ''}`}>▶</span>
                      <span>
                        <span className="font-medium">{c.name}</span>{' '}
                        <span className="text-xs text-gray-400 capitalize">
                          ({c.checklist_type?.replace('_', ' ')}
                          {!c.is_template && `, production ${c.production_checked_count ?? 0}/${c.total_count ?? 0} · quality ${c.checked_count ?? 0}/${c.total_count ?? 0}`}
                          {c.is_template ? ', template' : ''})
                        </span>
                        {!c.is_template && c.is_complete && (
                          <span className="ml-2 text-xs px-2 py-1 rounded bg-green-100 text-green-800 font-semibold">Complete</span>
                        )}
                        {!c.is_template && expandedId !== c.id && (
                          <span className="ml-2 text-xs text-komal-blue">Check items</span>
                        )}
                      </span>
                    </button>
                    {c.is_template && (
                      <div className="flex items-center gap-2">
                        <select
                          value={cloneTargets[c.id] || ''}
                          onChange={(e) => setCloneTargets({ ...cloneTargets, [c.id]: e.target.value })}
                          className="border border-gray-300 rounded px-2 py-1 text-xs"
                        >
                          <option value="">Clone to project...</option>
                          {projects.map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleClone(c.id)}
                          className="text-komal-blue hover:underline text-xs font-semibold"
                        >
                          Clone
                        </button>
                      </div>
                    )}
                  </div>

                  {expandedId === c.id && (
                    <div className="mt-3 border border-gray-200 rounded">
                      {(c.items || []).length === 0 ? (
                        <p className="text-gray-400 text-xs p-3">No items on this checklist.</p>
                      ) : (
                        <>
                          {!c.is_template && (
                            <div className="grid grid-cols-[1fr_70px_70px] gap-2 px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase border-b border-gray-100">
                              <span>Item</span>
                              <span className="text-center">Prod.</span>
                              <span className="text-center">Quality</span>
                            </div>
                          )}
                          <div className="divide-y divide-gray-100">
                            {c.items.map((item) => (
                              <div key={item.id} className="grid grid-cols-[1fr_70px_70px] gap-2 items-center px-3 py-2 text-xs">
                                <span>
                                  {item.description}
                                  {item.spec ? ` — ${item.spec}` : ''}
                                  {item.is_critical && <span className="ml-1 text-red-600 font-semibold">(critical)</span>}
                                  {item.production_checked && (
                                    <span className="block text-gray-400">production {formatDateDDMMYY(item.production_checked_date)}</span>
                                  )}
                                  {item.quality_checked && (
                                    <span className="block text-gray-400">quality {formatDateDDMMYY(item.quality_checked_date)}</span>
                                  )}
                                </span>
                                {c.is_template ? (
                                  <span />
                                ) : (
                                  <input
                                    type="checkbox"
                                    className="mx-auto block"
                                    checked={!!item.production_checked}
                                    onChange={() => handleToggleItem(c.id, item, 'production')}
                                  />
                                )}
                                {c.is_template ? (
                                  <span />
                                ) : (
                                  <input
                                    type="checkbox"
                                    className="mx-auto block"
                                    checked={!!item.quality_checked}
                                    disabled={!item.production_checked}
                                    title={!item.production_checked ? 'Production must check this first' : ''}
                                    onChange={() => handleToggleItem(c.id, item, 'quality')}
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default QualityChecklistsPage;
